/**
 * Contracts for module "crm". Owner: crm. Mounted at /v1/crm (CRM_DB).
 * Money is INTEGER minor units + currency. Restricted fields (salary, invoice financials, product cost)
 * are omitted server-side for callers without the Finance/HR CRM role (QM-C01-002).
 */
import { z } from 'zod';

const Str = (max = 200) => z.string().trim().min(1).max(max);
const OptStr = (max = 500) => z.string().trim().max(max).nullable().optional();
const Currency = z.string().regex(/^[A-Z]{3}$/).default('AED');
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const CRM_ROLES = ['sales', 'support', 'finance', 'hr', 'manager'] as const;
export type CrmRole = (typeof CRM_ROLES)[number];
export const CrmRoleGrantSchema = z.object({ userId: Str(64), role: z.enum(CRM_ROLES) });

export const CONSENT_STATES = ['opt_in', 'unknown', 'stop'] as const;
export type ConsentState = (typeof CONSENT_STATES)[number];

export const API_KEY_SCOPES = ['crm:read', 'crm:write'] as const;
export type ApiKeyScope = (typeof API_KEY_SCOPES)[number];

/** Restricted field registry: field -> CRM roles allowed to read/write it. */
export const RESTRICTED_FIELDS = {
  employee: { salaryMinor: ['finance', 'hr'] },
  invoice: { subtotalMinor: ['finance'], taxMinor: ['finance'], adjustmentsMinor: ['finance'], totalMinor: ['finance'], taxRateBps: ['finance'], items: ['finance'], adjustments: ['finance'] },
  product: { costMinor: ['finance'] },
  purchaseOrder: { totalMinor: ['finance'], items: ['finance'] },
} as const satisfies Record<string, Record<string, readonly CrmRole[]>>;

export const CompanyInputSchema = z.object({
  name: Str(), industry: OptStr(100), website: OptStr(300), phone: OptStr(40), address: OptStr(500),
  size: z.number().int().min(0).max(10_000_000).nullable().optional(), notes: OptStr(4000), ownerId: OptStr(64),
  customFields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export const ContactInputSchema = z.object({
  name: Str(), email: z.string().trim().email().max(254).nullable().optional(), phone: OptStr(40), title: OptStr(200),
  companyId: OptStr(64), ownerId: OptStr(64), notes: OptStr(4000), consentState: z.enum(CONSENT_STATES).optional(),
  customFields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export const DEAL_STAGES = ['lead', 'qualified', 'proposal', 'negotiation', 'won', 'lost'] as const;
export const DealInputSchema = z.object({
  title: Str(), contactId: OptStr(64), companyId: OptStr(64), valueMinor: z.number().int().min(0).default(0), currency: Currency,
  stage: z.enum(DEAL_STAGES).default('lead'), closeDate: IsoDate.nullable().optional(), ownerId: OptStr(64), notes: OptStr(4000),
  customFields: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
});
export const ActivityInputSchema = z.object({
  title: Str(), type: z.enum(['call', 'meeting', 'task', 'note', 'email', 'whatsapp']).default('task'),
  status: z.enum(['open', 'done']).default('open'), dueDate: z.string().max(40).nullable().optional(),
  contactId: OptStr(64), dealId: OptStr(64), leadId: OptStr(64), ownerId: OptStr(64), notes: OptStr(4000),
});
export const ProjectInputSchema = z.object({
  name: Str(), description: OptStr(4000), status: z.enum(['planning', 'active', 'on_hold', 'completed', 'cancelled']).default('active'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'), startDate: IsoDate.nullable().optional(), endDate: IsoDate.nullable().optional(),
  budgetMinor: z.number().int().min(0).default(0), currency: Currency, companyId: OptStr(64),
});
export const TaskInputSchema = z.object({
  title: Str(), description: OptStr(4000), projectId: OptStr(64), leadId: OptStr(64),
  status: z.enum(['todo', 'in_progress', 'review', 'done']).default('todo'), priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  assignedTo: OptStr(64), dueAt: z.string().max(40).nullable().optional(),
});
export const ProductInputSchema = z.object({
  sku: OptStr(64), name: Str(), description: OptStr(4000), category: OptStr(100), unitPriceMinor: z.number().int().min(0).default(0),
  /** Finance only; stripped (and the attempt audited) for other roles */
  costMinor: z.number().int().min(0).optional(), currency: Currency, supplierName: OptStr(200),
  stockQty: z.number().int().min(0).default(0), unit: z.string().max(20).default('pcs'),
});
export const ProductRelationInputSchema = z.object({
  relatedProductId: Str(64), relation: z.enum(['bundle', 'alternative', 'accessory', 'replacement']),
});
export const EmployeeInputSchema = z.object({
  name: Str(), email: z.string().trim().email().max(254).nullable().optional(), phone: OptStr(40), department: OptStr(100), position: OptStr(100),
  /** Finance/HR only */
  salaryMinor: z.number().int().min(0).optional(), currency: Currency, hireDate: IsoDate.nullable().optional(),
  status: z.enum(['active', 'on_leave', 'terminated']).default('active'), userId: OptStr(64),
});

export const InvoiceLineInputSchema = z.object({
  description: Str(500),
  /** quantity with up to 3 decimals */
  qty: z.number().positive().max(1_000_000).refine((q) => Math.abs(Math.round(q * 1000) - q * 1000) < 1e-6, 'max 3 decimals'),
  unitPriceMinor: z.number().int().min(0).optional(),
  productId: OptStr(64),
  poItemId: OptStr(64),
});
/** Client-sent subtotal/tax/total are accepted by the parser but ignored: totals are computed on the server. */
export const InvoiceInputSchema = z.object({
  invoiceNumber: OptStr(64), contactId: OptStr(64), companyId: OptStr(64), dealId: OptStr(64),
  status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled']).default('draft'), currency: Currency,
  taxRateBps: z.number().int().min(0).max(10_000).default(0), issueDate: IsoDate.nullable().optional(), dueDate: IsoDate.nullable().optional(),
  notes: OptStr(4000), items: z.array(InvoiceLineInputSchema).min(1).max(500),
  subtotal: z.unknown().optional(), tax: z.unknown().optional(), total: z.unknown().optional(),
  subtotalMinor: z.unknown().optional(), taxMinor: z.unknown().optional(), totalMinor: z.unknown().optional(),
});
export const InvoiceAdjustmentInputSchema = z.object({ amountMinor: z.number().int().refine((n) => n !== 0, 'non-zero'), reason: z.string() });
export const InvoiceTotalsSchema = z.object({ subtotalMinor: z.number().int(), taxMinor: z.number().int(), adjustmentsMinor: z.number().int(), totalMinor: z.number().int() });

export const PurchaseOrderInputSchema = z.object({
  poNumber: OptStr(64), supplierName: Str(), supplierEmail: z.string().email().nullable().optional(), currency: Currency,
  orderDate: IsoDate.nullable().optional(), expectedDate: IsoDate.nullable().optional(), notes: OptStr(4000),
  items: z.array(z.object({ productId: Str(64), qty: z.number().int().positive().max(1_000_000), unitCostMinor: z.number().int().min(0).optional() })).min(1).max(500),
});
export const CampaignEventInputSchema = z.object({
  campaignRef: Str(120), channel: z.enum(['email', 'sms', 'whatsapp', 'social', 'ads']),
  event: z.enum(['sent', 'delivered', 'opened', 'clicked', 'replied', 'converted', 'unsubscribed']), occurredAt: z.string().max(40),
});

export const CustomFieldInputSchema = z.object({
  entity: z.enum(['company', 'contact', 'deal', 'lead']), fieldKey: z.string().regex(/^[a-z][a-z0-9_]{0,39}$/), label: Str(100),
  fieldType: z.enum(['text', 'number', 'date', 'boolean', 'select']).default('text'), options: z.array(z.string().max(100)).max(100).optional(),
});

export const ApiKeyInputSchema = z.object({
  name: Str(100), scopes: z.array(z.enum(API_KEY_SCOPES)).min(1), expiresInDays: z.number().int().min(1).max(365).default(90),
});
export const ApiKeyCreatedSchema = z.object({ id: z.string(), key: z.string(), preview: z.string(), scopes: z.array(z.enum(API_KEY_SCOPES)), expiresAt: z.string() });

// ---------- leads ----------
export const LEAD_CHANNELS = ['website', 'email', 'whatsapp', 'meta_lead_ads', 'linkedin', 'audit', 'leadgen', 'import', 'manual'] as const;
export type LeadChannel = (typeof LEAD_CHANNELS)[number];
export const ConsentEvidenceSchema = z.object({ id: z.string().max(200), text: z.string().max(2000).nullable(), granted: z.boolean() });
export const LeadInputSchema = z.object({
  name: OptStr(200), email: z.string().trim().email().max(254).nullable().optional(), phone: OptStr(40),
  companyName: OptStr(200), companySize: z.number().int().min(0).nullable().optional(), website: OptStr(300), jobTitle: OptStr(200),
  channel: z.enum(LEAD_CHANNELS).default('manual'), sourceId: OptStr(200),
  consentState: z.enum(CONSENT_STATES).optional(), consentEvidence: z.array(ConsentEvidenceSchema).max(20).optional(),
  engagement: z.object({ pageViews: z.number().int().min(0).optional(), replied: z.boolean().optional(), requestedDemo: z.boolean().optional() }).optional(),
});
export const LeadSignalSchema = z.object({ key: z.string(), weight: z.number(), messageKey: z.string(), text: z.string() });
export type LeadSignal = z.infer<typeof LeadSignalSchema>;

export const RoutingRuleInputSchema = z.object({
  name: Str(100), strategy: z.enum(['round_robin', 'fixed_owner']).default('round_robin'), team: OptStr(64), fixedOwnerId: OptStr(64),
  channels: z.array(z.enum(LEAD_CHANNELS)).default([]), followUpMinutes: z.number().int().min(5).max(10_080).default(60),
  priority: z.number().int().min(0).max(1000).default(100), active: z.boolean().default(true),
});
export const TeamMemberInputSchema = z.object({
  userId: Str(64), team: Str(64), displayName: OptStr(100), status: z.enum(['active', 'suspended']).default('active'),
  isManager: z.boolean().default(false), position: z.number().int().min(0).default(0),
});
export const SlaSettingsInputSchema = z.object({
  slaMinutes: z.number().int().min(5).max(10_080), utcOffsetMinutes: z.number().int().min(-720).max(840).default(240),
  businessDays: z.array(z.number().int().min(0).max(6)).default([1, 2, 3, 4, 5]),
  openTime: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(), closeTime: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(),
});

// ---------- CSV import ----------
export const CONTACT_IMPORT_FIELDS = ['name', 'email', 'phone', 'title', 'notes', 'company'] as const;
export const ImportInputSchema = z.object({
  csv: z.string().min(1).max(8_000_000),
  mapping: z.record(z.string(), z.enum(CONTACT_IMPORT_FIELDS)),
});

// ---------- queue events consumed by the CRM ----------
/** qm-jobs message type for audit buyers (QM-C02-002). Producer: billing/audit outbox. */
export const AUDIT_BUYER_EVENT = 'crm.audit_buyer.sync';
export const AuditBuyerPayloadSchema = z.object({
  orderRef: z.string().min(1).max(100),
  email: z.string().email().max(254),
  name: z.string().max(200).nullable().optional(),
  companyName: z.string().max(200).nullable().optional(),
  website: z.string().max(300).nullable().optional(),
  product: z.string().max(60).default('audit_pro'),
  // in_progress, delayed, delivered: qm-audit job progress (Phase 1 Track E, additive)
  orderStatus: z.enum(['requested', 'paid', 'complimentary', 'refunded', 'failed', 'in_progress', 'delayed', 'delivered']),
  occurredAt: z.string().max(40),
});
export type AuditBuyerPayload = z.infer<typeof AuditBuyerPayloadSchema>;

/**
 * qm-jobs message type for the WhatsApp "Move to deal" hand-off (Pending Build item 6). Producer: wa-core
 * (`POST /v1/whatsapp-leads/leads/:id/move-to-deal`). Consumer: qm-api jobs bridge -> crm `applyWhatsAppDeal`.
 * Message: `{ type, tenantId, idempotencyKey: 'wa-deal:<waLeadId>', payload: WhatsAppDealPayload }`.
 * The CRM creates or links a contact (by phone, then email) and a company (by name, case-insensitive), then creates
 * exactly one deal per WhatsApp lead (crm_inbox + UNIQUE crm_deal_sources). Carries PII (phone, email) that is needed
 * to create the contact; consumers must not log it (use hash + last 4 in diagnostics).
 */
export const WA_DEAL_EVENT = 'crm.deal.from_whatsapp';
export const waDealIdempotencyKey = (waLeadId: string) => `wa-deal:${waLeadId}`;
const PortalLink = z.string().max(200).regex(/^\/[A-Za-z0-9/_-]*$/);
export const WhatsAppDealPayloadSchema = z.object({
  handoffId: z.string().min(1).max(64),
  waLeadId: z.string().min(1).max(64),
  conversationId: z.string().max(64).nullable(),
  /** WhatsApp id (international digits, no +) */
  waId: z.string().regex(/^\d{7,20}$/),
  name: z.string().max(200).nullable(),
  email: z.string().email().max(254).nullable(),
  companyName: z.string().max(200).nullable(),
  title: z.string().trim().min(1).max(200),
  valueMinor: z.number().int().min(0).default(0),
  currency: Currency,
  stage: z.enum(DEAL_STAGES),
  ownerId: z.string().max(64).nullable(),
  requestedBy: z.string().min(1).max(64),
  /** WhatsApp consent mapped to CRM states; the strictest of this and any linked contact's state is kept */
  consent: z.enum(CONSENT_STATES),
  qualification: z.object({
    service: z.string().max(200).nullable(),
    budget: z.string().max(100).nullable(),
    timeline: z.string().max(100).nullable(),
    waStage: z.string().max(20),
    percent: z.number().int().min(0).max(100),
    missing: z.array(z.string().max(40)).max(10),
  }),
  /** portal-relative link back to the conversation (or the lead when there is no conversation) */
  link: PortalLink,
});
export type WhatsAppDealPayload = z.infer<typeof WhatsAppDealPayloadSchema>;
export interface WhatsAppDealResult {
  status: 'applied' | 'duplicate' | 'rejected';
  dealId: string | null;
  contactId: string | null;
  companyId: string | null;
  contactCreated: boolean;
  companyCreated: boolean;
  /** set when status = rejected (permanent: retrying will not help) */
  reason?: 'plan_required';
}

export const AssistantQuestionSchema = z.object({ question: z.string().trim().min(3).max(1000) });

// ---------- list query ----------
export const ListQuerySchema = z.object({
  cursor: z.string().max(500).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  fields: z.string().max(500).optional(),
});

// ---------- pure lead helpers shared by crm + leadgen (no I/O) ----------
export function normalizeEmail(email: string | null | undefined): string | null {
  const e = (email ?? '').trim().toLowerCase();
  return e && e.includes('@') ? e : null;
}
/** Digits only, leading 00 -> international; UAE local 05x -> 9715x. Returns null when < 7 digits. */
export function normalizePhone(phone: string | null | undefined): string | null {
  let d = (phone ?? '').replace(/[^\d+]/g, '');
  if (d.startsWith('+')) d = d.slice(1);
  else if (d.startsWith('00')) d = d.slice(2);
  else if (/^05\d{8}$/.test(d)) d = `971${d.slice(1)}`;
  d = d.replace(/\D/g, '');
  return d.length >= 7 ? d : null;
}
const CONSENT_RANK: Record<ConsentState, number> = { opt_in: 0, unknown: 1, stop: 2 };
/** The strictest consent wins (STOP beats unknown beats opt-in) - QM-A02-005. */
export function strictestConsent(...states: (ConsentState | null | undefined)[]): ConsentState {
  let out: ConsentState = 'opt_in';
  let seen = false;
  for (const s of states) {
    if (!s) continue;
    seen = true;
    if (CONSENT_RANK[s] > CONSENT_RANK[out]) out = s;
  }
  return seen ? out : 'unknown';
}

export interface LeadScoreInput {
  email?: string | null; phone?: string | null; companyName?: string | null; companySize?: number | null;
  website?: string | null; jobTitle?: string | null; channel?: string | null; consentState?: ConsentState | null;
  engagement?: { pageViews?: number; replied?: boolean; requestedDemo?: boolean } | null;
}
export interface LeadScoreSignal { key: string; weight: number; messageKey: string; params?: Record<string, string | number> }
export interface LeadScore {
  score: number; temperature: 'hot' | 'warm' | 'cold'; qualification: 'sql' | 'mql' | 'unqualified';
  confidence: 'high' | 'low'; signals: LeadScoreSignal[];
}
const SENIOR = /\b(ceo|cto|cmo|coo|cfo|founder|owner|director|head|vp|president|partner|gm|general manager)\b/i;
const FREE_MAIL = /@(gmail|yahoo|hotmail|outlook|icloud|live|aol|proton(mail)?)\./i;

/**
 * Deterministic, explainable lead score 0-100 (QM-A02-003). Every point comes from a named signal so the
 * explanation is the scoring itself. Missing company size -> a zero-weight 'company size unknown' signal
 * and confidence 'low'.
 */
export function scoreLead(l: LeadScoreInput): LeadScore {
  const s: LeadScoreSignal[] = [];
  const add = (key: string, weight: number, params?: Record<string, string | number>) => s.push({ key, weight, messageKey: `crm.leads.signal.${key}`, params });
  let missing = 0;
  if (l.engagement?.requestedDemo) add('requested_demo', 30);
  if (l.engagement?.replied) add('replied', 15);
  if ((l.engagement?.pageViews ?? 0) >= 5) add('high_engagement', 10, { views: l.engagement!.pageViews! });
  if (l.channel === 'audit') add('audit_buyer', 25);
  else if (l.channel === 'whatsapp' || l.channel === 'website') add('inbound_channel', 10, { channel: l.channel });
  if (l.jobTitle && SENIOR.test(l.jobTitle)) add('senior_title', 15);
  if (l.companySize === null || l.companySize === undefined) { add('company_size_unknown', 0); missing++; }
  else if (l.companySize >= 50) add('company_size_fit', 15, { size: l.companySize });
  else if (l.companySize >= 10) add('company_size_small', 8, { size: l.companySize });
  const email = normalizeEmail(l.email);
  if (email && !FREE_MAIL.test(email)) add('business_email', 10);
  else if (!email) missing++;
  if (normalizePhone(l.phone)) add('phone_present', 5);
  if (l.website) add('website_present', 5);
  if (l.consentState === 'stop') add('opted_out', -40);
  const score = Math.max(0, Math.min(100, s.reduce((a, x) => a + x.weight, 0)));
  const temperature = score >= 60 ? 'hot' : score >= 30 ? 'warm' : 'cold';
  const qualification = score >= 60 ? 'sql' : score >= 30 ? 'mql' : 'unqualified';
  const confidence = missing > 0 ? 'low' : 'high';
  // unknown-data signals always stay visible in the explanation; then by absolute weight
  s.sort((a, b) => Number(b.key.endsWith('_unknown')) - Number(a.key.endsWith('_unknown')) || Math.abs(b.weight) - Math.abs(a.weight));
  return { score, temperature, qualification, confidence, signals: s };
}
